import {
  SHBox,
  SHButton,
  SHHtmlBlock,
  SHStack,
} from "@components/design-systems";
import { DownloadSVG, TooltipAllowSVG } from "@components/svgs";
import { DELAY_TIME } from "@constants";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisUserType } from "@models/platform-analysis/entities/analysis";
import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import { hexToRGBA } from "@utils";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useUserPermissions } from "@hooks/userUserPermission";
import { PDFDocument } from "./components/document";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import {
  summaryFeatureAnalysisShortlistedTableRowsSelectors as featureRowSelectors,
  feeAnalysisSmaTableRowsSelectors,
  disqualifiedFeeAnalysisSmaTableRowsSelectors,
} from "@redux/slices/platform-analysis";
import { summaryBusinessMetricAnalysisShortlistedTableRowsSelectors as businessRowSelectors } from "@redux/slices/platform-analysis";

interface ExportPDFButtonProps {
  isCompleted?: boolean;
  isPlatformAnalysis?: boolean;
}

export const ExportPDFButton = ({
  isCompleted,
}: ExportPDFButtonProps): JSX.Element => {
  const { platformAnalysis } = usePlatformAnalysis();
  const { palette, shadows } = useTheme();

  const analysisSteps = useSelector(
    (state: RootState) => state.platformAnalysis.analysisSteps,
  );
  const productRecommendations = useSelector(
    (state: RootState) =>
      state.platformAnalysis.internal.summary.productRecommendations,
  );
  const hiddenIdsIndexMap = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.hiddenIdsIndexMap,
  );
  const disqualifiedHiddenIdsIndexMap = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis
        ?.disqualifiedHiddenIdsIndexMap,
  );
  const hiddenIds = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.hiddenIds,
  );
  const disqualifiedHiddenIds = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.disqualifiedHiddenIds,
  );
  const feeSubProducts = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.subProductFiltered,
  );
  const disqualifiedFeeSubProducts = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis
        ?.disqualifiedSubProductFiltered,
  );
  const featureAnalysisTableRows = useSelector(featureRowSelectors.selectAll);
  const businessMetricTableRows = useSelector(businessRowSelectors.selectAll);
  const disclaimers = useSelector(
    (state: RootState) => state.platformAnalysis?.disclaimers,
  );
  const smaTableRows = useSelector(feeAnalysisSmaTableRowsSelectors.selectAll);
  const disqualifiedSmaTableRows = useSelector(
    disqualifiedFeeAnalysisSmaTableRowsSelectors.selectAll,
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const [isDisable, setIsDisable] = useState(false);

  const { isSupplierGroup } = useUserPermissions();

  const userType: AnalysisUserType = isSupplierGroup ? "supplier" : "adviser";

  useEffect(() => {
    setIsDisable(isDownloading || !isCompleted || !platformAnalysis);
  }, [isDownloading, isCompleted, platformAnalysis]);

  const handleOnDownloadPDF = async () => {
    try {
      if (!platformAnalysis) return;

      const blob = await pdf(
        <PDFDocument
          userType={userType}
          analysis={platformAnalysis}
          analysisSteps={analysisSteps}
          productRecommendations={productRecommendations}
          hiddenIdsIndexMap={hiddenIdsIndexMap}
          disqualifiedHiddenIdsIndexMap={disqualifiedHiddenIdsIndexMap}
          hiddenIds={hiddenIds}
          disqualifiedHiddenIds={disqualifiedHiddenIds}
          feeSubProducts={feeSubProducts}
          disqualifiedFeeSubProducts={disqualifiedFeeSubProducts}
          featureAnalysisTableRows={featureAnalysisTableRows}
          businessMetricTableRows={businessMetricTableRows}
          disclaimers={disclaimers}
          smaTableRows={smaTableRows}
          disqualifiedSmaTableRows={disqualifiedSmaTableRows}
        />,
      ).toBlob();

      saveAs(
        blob,
        `SuitabilityHub Platform Analysis Summary - ${platformAnalysis?.summary?.name}.pdf`,
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "transparent",
      paddingRight: "0px",
    },
    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
      {
        marginTop: "-1px",
      },
  }));

  return (
    <TooltipStyled
      placement="top-end"
      enterDelay={DELAY_TIME}
      enterNextDelay={DELAY_TIME}
      title={
        isDisable && !isDownloading ? (
          <SHStack
            sx={{
              p: 1,
              mt: "3px",
              maxWidth: "190px",
              minHeight: "20px",
              bgcolor: hexToRGBA(palette.common.white, 0.85),
              border: "1px solid #E3E3E3",
              backdropFilter: "blur(2px)",
              borderRadius: "3px",
              boxShadow: shadows[1],
              position: "relative",
            }}
          >
            <SHHtmlBlock
              variant="body3"
              color={palette.text.disabled}
              content={"Complete your analysis to download it as PDF"}
              textAlign={"left"}
            />
            <SHBox sx={{ position: "absolute", top: "-8px", left: "82%" }}>
              <TooltipAllowSVG transform={"rotate(180)"} />
            </SHBox>
          </SHStack>
        ) : (
          ""
        )
      }
    >
      <SHBox component={"span"}>
        <SHButton
          variant="contained"
          size="extraMedium"
          startIcon={<DownloadSVG />}
          disabled={isDownloading}
          isLoading={isDownloading}
          onClick={() => {
            setIsDownloading(true);
            setTimeout(() => handleOnDownloadPDF(), 0);
          }}
          sx={{
            "&.Mui-disabled.sh-btn-with-icon": {
              border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
              backgroundColor: palette.primary[50],
              color: palette.common.white,
            },
          }}
        >
          PDF
        </SHButton>
      </SHBox>
    </TooltipStyled>
  );
};
